import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { Form, Modal, Spin } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useMatch, useParams, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { getBooking, cleanedBooking, cleanClientId, cleanedExistingClientList, setClientId, refreshClientBookingDetails } from "store/bookings"
import { fetchCommunicationNotificationStatus } from "store/communication"
import BookingForm from "../BookingForm"
import { resetExperienceBookingData, resetExperienceById, fetchExperiences } from "store/experiences"
import { fetchReservationDetail } from "store/reservations"
import { selectedPaymentMethodIdSelector, setSelectedPaymentMethodId, cleanedPaymentMethods } from "store/user"
import { useStripe } from "@stripe/react-stripe-js"
import { submitTripBooking, populateFormWithBooking, hasNoUserBookingChanges } from "../bookingUtils"
import { useDevice } from "hooks/useDevice"
import { useStripeIssue } from "hooks/useStripeIssue"
import { toaster } from "components/common/Toast"
import { numberToCurrency } from "helpers/string"
import { ResponsivePage } from "ui/ResponsivePage"
import { routes } from "router"
import DiscardConfirm from "ui/DiscardModal"
import EditBookingForm from "../BookingForm/EditBookingForm"
import { useWatch } from "antd/es/form/Form"

const GuideBookingWindow = () => {
  const [form] = Form.useForm()
  const { id: bookingUuid } = useParams()
  const location = useLocation()
  const client = location?.state?.client
  const filterExperienceIds = location?.state?.filterExperienceIds // From equipment details page
  const { isMobile } = useDevice()
  const stripe = useStripe()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isStripeChargeable, stripeProblemMessage } = useStripeIssue()
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const isDirtyRef = useRef(false)
  const [appliedPromos, setAppliedPromos] = useState([])
  const [isOriginalDiscountRemoved, setIsOriginalDiscountRemoved] = useState(false)
  const [bookingSelections, setBookingSelections] = useState({
    date: null, timeSlot: null, tripLength: null, counts: null,
    amount: 0, baseAmount: 0, breakdown: [], addOns: [], addOnsTotal: 0
  })
  const updateBookingSelections = useCallback(
    (updates) => setBookingSelections((prev) => ({ ...prev, ...updates })),
    []
  )
  const handlePromoApplied = (promo) => setAppliedPromos((prev) => [...prev, promo])
  const handlePromoRemoved = (code) => setAppliedPromos((prev) => prev.filter((p) => p.code !== code))
  const handleClearPromos = () => setAppliedPromos([])

  const isBookAgain = useMatch(routes.guideBookingBookAgainPath())
  const isEdit = useMatch(routes.guideBookingEditPath())
  const [bookingModalVisible, setBookingModalVisible] = useState(true)
  const [formLoading, setFormLoading] = useState(!!bookingUuid)
  const [retryBookingUuid, setRetryBookingUuid] = useState(null)
  const { booking } = useSelector((store) => store.bookings)
  const clientId = useSelector((state) => state.bookings.clientId)
  const selectedPaymentMethodId = useSelector(selectedPaymentMethodIdSelector)
  const { experienceDetail, loading: experienceDetailLoading } = useSelector((state) => state.experiences.experienceById)

  // DS-4473. Recovering a booking request that expired before it could be accepted. The edit
  // screen is reused as-is; what changes is that the save also accepts the reservation, and the
  // fields that aren't driven by the new date/time render read-only.
  //
  // recovery_allowed lives on the reservation detail endpoint, not on the /trip_bookings payload
  // this screen loads, so it's fetched on demand — only for an expired request, leaving every
  // ordinary edit with the same number of calls it makes today.
  const isExpiredRequest = Boolean(isEdit) && !isBookAgain && booking?.status === "requested" && Boolean(booking?.is_expired)
  // DS-4473. The other restricted mode: a completed booking opens this same modal to take charges
  // discovered after the trip. Only add-ons and price move; date, guests, experience and check-in
  // state are frozen, so the booking stays Completed with an outstanding balance.
  const isCompletedBooking = Boolean(isEdit) && !isBookAgain && booking?.status === "completed"
  const { data: reservationDetail } = useSelector((state) => state.reservations.detail)
  // Both eligibility flags live on the reservation detail endpoint, not on the /trip_bookings
  // payload this screen loads — fetched only for the two cases that need them, so an ordinary edit
  // makes the same calls it makes today.
  useEffect(() => {
    if ((isExpiredRequest || isCompletedBooking) && bookingUuid) dispatch(fetchReservationDetail(bookingUuid))
  }, [isExpiredRequest, isCompletedBooking, bookingUuid, dispatch])
  const isDetailForThisBooking = reservationDetail?.uuid === booking?.uuid
  // An expired request outside its 24h window must not open in recovery mode — the backend would
  // refuse the save anyway, and offering the flow implies it will work.
  const isRecovery = isExpiredRequest && isDetailForThisBooking && Boolean(reservationDetail?.canRecover)
  const isPostTrip = isCompletedBooking && isDetailForThisBooking && Boolean(reservationDetail?.canAdjustPostTrip)
  // While editing, keep the modal overlay spinning until the experience detail API resolves so the
  // duration/time fields don't flash before the experience type (single_day/multi_day) is known.
  const editDetailLoading = !!isEdit && !isBookAgain && (experienceDetailLoading || !experienceDetail)

  // The experience is editable while editing. When it's swapped away from the booking's original
  // trip, the old discount and add-ons no longer apply: drop the original discount below and clear
  // any user-applied promos (add-ons are cleared in ExperienceDetail on change). New add-ons and
  // discounts can then be added against the new experience.
  // preserve: true — the "experience" Form.Item lives only in ExperienceDetail (Step 1) and unmounts
  // on Step 2. Without preserve, useWatch reads only currently-mounted fields and silently reports
  // undefined once Step 2 mounts, making isExperienceChanged flip back to false and re-show a coupon
  // that no longer applies to the newly selected experience.
  const selectedExperienceUuid = useWatch("experience", { form, preserve: true })?.value
  const isExperienceChanged =
    !!isEdit && !isBookAgain && !!booking?.trip?.uuid && !!selectedExperienceUuid && selectedExperienceUuid !== booking.trip.uuid

  // Clear on ANY experience switch, not just a deviation from the booking's original experience —
  // a promo applied while on experience B is invalid once the guide picks experience C, or even
  // switches back to A, since it was only ever validated against B.
  const previousExperienceUuidRef = useRef(selectedExperienceUuid)
  useEffect(() => {
    const previousExperienceUuid = previousExperienceUuidRef.current
    previousExperienceUuidRef.current = selectedExperienceUuid
    if (previousExperienceUuid && selectedExperienceUuid && previousExperienceUuid !== selectedExperienceUuid) {
      setAppliedPromos([])
    }
  }, [selectedExperienceUuid])

  const originalPromo = useMemo(() => {
    if (!isEdit || isExperienceChanged) return null
    // The discount RULE comes from booking.discount_details (booking detail API). Fall back to
    // payment_summary for older shapes.
    const detail = booking?.discount_details
    const paymentSummary = booking?.payment_summary ?? {}
    const code = detail?.code ?? paymentSummary?.discount_code
    if (!code) return null
    const value = detail?.value ?? paymentSummary?.discount_value
    const discountType = detail?.discount_type ?? paymentSummary?.discount_type
    const applyToAddOns =
      detail?.applicability_rules?.apply_to_add_ons ??
      paymentSummary?.applicability_rules?.apply_to_add_ons ??
      paymentSummary?.apply_to_add_ons ??
      false
    const label =
      discountType === "percentage"
        ? `${value}% off`
        : `$${Number(value ?? paymentSummary?.discount_amount ?? 0).toFixed(2)} off`
    const base = { type: "discount", code, name: detail?.name || code, label }

    // Until the user changes guests/add-ons, show the discount the backend actually applied
    // (payment_summary.discount_amount) so it matches the saved total exactly — recomputing it on
    // the frontend can drift. Once something changes, carry the RULE so the discount recomputes
    // against the new base (guest subtotal + add-ons, per apply_to_add_ons).
    const ready = !!bookingSelections?.counts
    const unchanged = !ready || hasNoUserBookingChanges(bookingSelections, booking)
    if (unchanged || value == null || !discountType) {
      return { ...base, discountAmount: Math.max(0, Number(paymentSummary?.discount_amount || 0)) }
    }
    return { ...base, value: Number(value), discount_type: discountType, apply_to_add_ons: applyToAddOns }
  }, [isEdit, isExperienceChanged, booking, bookingSelections])

  const effectiveAppliedPromos = useMemo(() => {
    if (!isEdit || isOriginalDiscountRemoved || !originalPromo) return appliedPromos
    return [originalPromo, ...appliedPromos]
  }, [isEdit, isOriginalDiscountRemoved, originalPromo, appliedPromos])

  const handleEditPromoRemoved = (code) => {
    if (isEdit && code === booking?.payment_summary?.discount_code && !isOriginalDiscountRemoved) {
      setIsOriginalDiscountRemoved(true)
    } else {
      handlePromoRemoved(code)
    }
  }

  const handleCancel = () => {
    if (isDirtyRef.current) {
      setShowDiscardConfirm(true)
    } else {
      closeForm()
    }
  }

  const closeForm = () => {
    isDirtyRef.current = false
    form.resetFields()
    setBookingSelections({ date: null, timeSlot: null, tripLength: null, counts: null, amount: 0, baseAmount: 0, breakdown: [], addOns: [], addOnsTotal: 0 })
    dispatch(cleanClientId())
    dispatch(cleanedPaymentMethods())
    dispatch(setSelectedPaymentMethodId(""))
    dispatch(resetExperienceBookingData())
    dispatch(resetExperienceById())
    dispatch(cleanedExistingClientList())
    dispatch(cleanedBooking())
    setBookingModalVisible(false)
    navigate(-1)
  }

  const handleDiscardCancel = () => {
    setShowDiscardConfirm(false)
  }

  const handleDiscardConfirm = () => {
    setShowDiscardConfirm(false)
    closeForm()
  }

  const handleSubmit = async () => {
    // Defense in depth: the Stripe payment options are disabled in the UI when Stripe can't charge,
    // but that only removes the affordance. Refuse to submit a Stripe charge here too, so re-enabling
    // the option via dev tools can't push a payment through. (The backend remains the authoritative guard.)
    const paymentType = form.getFieldValue("paymentType")
    const usesStripe = paymentType === "card" || paymentType === "link"
    const willCollectPayment = !isEdit || form.getFieldValue("collectPayment") === "yes"
    if (usesStripe && willCollectPayment && !isStripeChargeable) {
      toaster.error(stripeProblemMessage || t("form_validation.payment_provider_missing"))
      return
    }

    setFormLoading(true)
    try {
      const result = await submitTripBooking({
        form,
        dispatch,
        stripe,
        bookingSelections,
        selectedPaymentMethodId,
        bookingUuid: retryBookingUuid || bookingUuid,
        isBookAgain,
        isEdit,
        clientId,
        client,
        booking,
        appliedPromos,
        isRecovery,
        isPostTrip
      })

      // Recovery can succeed with the money still outstanding — the slot is committed atomically,
      // the charge isn't. Say which happened instead of a blanket success, so nobody reads a
      // declined card as collected.
      if (isRecovery) {
        const outstanding = Number(result?.payment_summary?.remaining_balance ?? result?.data?.payment_summary?.remaining_balance ?? 0)
        if (outstanding > 0) {
          toaster.success(t("booking.recovery.success_balance_due", { amount: numberToCurrency(outstanding) }))
        } else {
          toaster.success(t("booking.recovery.success"))
        }
      }

      // Clear retryBookingUuid only if booking is successful
      setRetryBookingUuid(null)

      // The details view we return to reads `clientBookingDetails` (the /booking_detail
      // endpoint), which the update did not refresh. Re-fetch it — with retry, since that
      // endpoint can briefly return the pre-update snapshot — so it shows the latest data
      // instead of staying stale until a manual refresh. Awaited so the slice is fresh before
      // we navigate back. (Skip for book-again, which creates a separate new booking.)
      if (isEdit && !isBookAgain && bookingUuid) {
        await dispatch(refreshClientBookingDetails(bookingUuid))
      }

      // Close modal or reset as needed
      closeForm()
    } catch (err) {
      if (err?.retryBookingUuid) {
        setRetryBookingUuid(err?.retryBookingUuid)
      } else {
        console.error("Booking failed:", err)
      }
    }

    setFormLoading(false)
  }

  useEffect(() => {
    return () => {
      dispatch(cleanClientId())
      dispatch(cleanedPaymentMethods())
      dispatch(setSelectedPaymentMethodId(""))
      dispatch(resetExperienceBookingData())
      dispatch(resetExperienceById())
      dispatch(cleanedExistingClientList())
      dispatch(cleanedBooking())
    }
  }, [dispatch])

  // Fetch the experience list for the experience dropdown. Needed for new bookings, book-again, and
  // now edit too — the experience is editable while editing an existing booking, so the full list
  // must be available to switch to.
  useEffect(() => {
    dispatch(fetchExperiences("booking_window"))
  }, [dispatch])

  useEffect(() => {
    const fetchAndPopulate = async () => {
      if (!bookingUuid) return
      dispatch(cleanedBooking())
      dispatch(resetExperienceById())
      dispatch(resetExperienceBookingData())
      form.resetFields()
      setFormLoading(true)

      try {
        const [res, notificationRes] = await Promise.all([
          dispatch(getBooking(bookingUuid, true, isBookAgain)),
          // dispatch(fetchCommunicationNotificationStatus())
        ])
        const bookingData = res?.payload || res

        if (bookingData) {
          const initialSelections = await populateFormWithBooking({ booking: bookingData, bookingUuid, dispatch, form, isBookAgain })
          if (initialSelections) setBookingSelections(initialSelections)
          if (!isBookAgain) {
            const isLocked = bookingData?.booking_source === "marketplace" || bookingData?.booking_source === "website"
            const notifyDefault = isLocked ? true : (notificationRes?.data?.edit_booking_email_allowed ?? true)
            form.setFieldsValue({ client_notify: notifyDefault, send_guest_email: notifyDefault })
          }
          isDirtyRef.current = false
        } else {
          console.warn("No booking returned to populate")
        }
      } catch (err) {
        console.error("Error in fetchAndPopulate", err)
      } finally {
        setFormLoading(false)
      }
    }

    fetchAndPopulate()
  }, [bookingUuid, isBookAgain, dispatch, form])

  useEffect(() => {
    if (client?.user_id) {
      form.setFieldsValue({
        firstName: client?.user?.first_name,
        lastName: client?.user?.last_name,
        email: client?.user?.email,
        phone_number: { fullNumber: client?.user?.phone_number, countryCode: client?.user?.country_code }
      })
      dispatch(setClientId(client?.user_id))
    }
  }, [dispatch, form, client])

  const handleValuesChange = () => {
    isDirtyRef.current = true
  }
  const title = isEdit && !isBookAgain ? t("bookings.edit") : t("bookings.add")

  return (
    <>
      {isMobile ? (
        <ResponsivePage onBackClick={handleCancel} pageTitle={title}>
          <Spin spinning={formLoading || editDetailLoading}>
            {isEdit ? (
              <EditBookingForm
                form={form}
                bookingUuid={bookingUuid}
                isEdit={isEdit}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onCancelModal={handleCancel}
                bookingModalVisible={bookingModalVisible}
                onValuesChange={handleValuesChange}
                appliedPromos={effectiveAppliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handleEditPromoRemoved}
                isExperienceChanged={isExperienceChanged}
                bookingSelections={bookingSelections}
                updateBookingSelections={updateBookingSelections}
                isRecovery={isRecovery}
                isPostTrip={isPostTrip}
              />
            ) : (
              <BookingForm
                form={form}
                bookingUuid={bookingUuid}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onCancelModal={handleCancel}
                bookingModalVisible={bookingModalVisible}
                filterExperienceIds={filterExperienceIds}
                appliedPromos={appliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handlePromoRemoved}
                onClearPromos={handleClearPromos}
                onValuesChange={handleValuesChange}
                bookingSelections={bookingSelections}
                updateBookingSelections={updateBookingSelections}
              />
            )}
          </Spin>
        </ResponsivePage>
      ) : (
        <Modal
          width={1000}
          title={title}
          open={bookingModalVisible}
          onCancel={handleCancel}
          footer={null}
          maskClosable={false}
          closable={true}
        >
          <Spin spinning={formLoading || editDetailLoading}>
            {isEdit ? (
              <EditBookingForm
                form={form}
                bookingUuid={bookingUuid}
                isEdit={isEdit}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onCancelModal={handleCancel}
                bookingModalVisible={bookingModalVisible}
                onValuesChange={handleValuesChange}
                appliedPromos={effectiveAppliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handleEditPromoRemoved}
                isExperienceChanged={isExperienceChanged}
                bookingSelections={bookingSelections}
                updateBookingSelections={updateBookingSelections}
                isRecovery={isRecovery}
                isPostTrip={isPostTrip}
              />
            ) : (
              <BookingForm
                form={form}
                bookingUuid={bookingUuid}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onCancelModal={handleCancel}
                bookingModalVisible={bookingModalVisible}
                filterExperienceIds={filterExperienceIds}
                appliedPromos={appliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handlePromoRemoved}
                onClearPromos={handleClearPromos}
                onValuesChange={handleValuesChange}
                bookingSelections={bookingSelections}
                updateBookingSelections={updateBookingSelections}
              />
            )}
          </Spin>
        </Modal>
      )}

      {/* Discard Confirmation Modal */}
      <DiscardConfirm
        open={showDiscardConfirm}
        onCancel={handleDiscardCancel}
        onConfirm={handleDiscardConfirm}
        title={t("Discard Changes?")}
        description={t("global.discard_changes")}
        t={t}
      />
    </>
  )
}

export default GuideBookingWindow
